import React from 'react';
import { motion } from 'framer-motion';
import { Globe2Icon, MailIcon, MessageCircleIcon, MessageSquareTextIcon, MonitorUpIcon, QrCodeIcon, Share2Icon, UsersIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
const COLUMVI_CHANNEL_IMAGE = 'https://cdn.magicpatterns.com/uploads/drdztpnvaoUPvSdPcUuzwR/Screenshot_2026-08-27_at_10.57.18.png';
const CHANNELS = [{
  label: 'Embed',
  detail: 'Websites and portals',
  icon: Globe2Icon
}, {
  label: 'Text',
  detail: 'SMS and direct links',
  icon: MessageSquareTextIcon
}, {
  label: 'WhatsApp',
  detail: 'Conversation-ready',
  icon: MessageCircleIcon
}, {
  label: 'QR Code',
  detail: 'Print and events',
  icon: QrCodeIcon
}, {
  label: 'Post',
  detail: 'Organic and paid social',
  icon: Share2Icon
}, {
  label: 'Email',
  detail: 'CRM and newsletters',
  icon: MailIcon
}, {
  label: 'Advertise',
  detail: 'Banners and paid media',
  icon: MonitorUpIcon
}, {
  label: 'Congress',
  detail: 'Booths and sessions',
  icon: UsersIcon
}] as const;
export function Slide03Background({}: SlideProps) {
  return <InteriorSlide surface data-id="element-81" data-mp="3dpitah20szb5">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.55
    }} className="mb-[clamp(1.25rem,2vw,2rem)] flex-shrink-0" data-id="element-82" data-mp="3dsdc645dyc2g">
        <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-83" data-mp="34ryqv4sf8wyy">
          Channel-agnostic distribution
        </p>
        <h2 className="max-w-5xl text-[clamp(2rem,3.8vw,3.6rem)] font-extrabold leading-[1.02] tracking-[-0.04em] text-[var(--fg)]" data-id="element-84" data-mp="bxk35z7zxou">
          One experience.
          <span className="text-[var(--brand)]" data-id="element-85" data-mp="3nozxsv5jd75j"> More channels to share.</span>
        </h2>
        <p className="mt-3 max-w-3xl text-[clamp(0.95rem,1.2vw,1.1rem)] leading-relaxed text-[var(--muted)]" data-id="element-86" data-mp="o0qbqq8t7jdn">
          A single Snak can meet HCPs wherever attention already exists—without
          rebuilding the experience for every channel.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-7 lg:grid-cols-12 lg:gap-10" data-id="element-87" data-mp="2dkgf68onrbwo">
        <motion.article initial={{
        opacity: 0,
        x: -24
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.18,
        duration: 0.6
      }} className="relative flex min-h-0 flex-col overflow-hidden rounded-3xl bg-[var(--brand)] p-[clamp(1.25rem,2vw,2rem)] text-white shadow-lg lg:col-span-5" data-id="element-88" data-mp="2ij9u85jlfayy">
          <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full border-[34px] border-white/10" data-id="element-89" data-mp="1zr8lmbh0o6cc" />
          <div className="relative flex items-center gap-3" data-id="element-90" data-mp="1ku84f1wgbts4">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/15" data-id="element-91" data-mp="1kvsv2gfnaskp">
              <MessageCircleIcon className="h-6 w-6" aria-hidden="true" data-id="element-92" data-mp="1pucikl9w0cf6" />
            </span>
            <div data-id="element-93" data-mp="3f5mtrhljvr7c">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-white/65" data-id="element-94" data-mp="3k6ajlzy1m1zz">
                Example entry point
              </p>
              <h3 className="text-2xl font-extrabold" data-id="element-95" data-mp="18jyy43ettd0k">WhatsApp</h3>
            </div>
          </div>

          <div className="relative mx-auto mt-5 min-h-0 w-full max-w-sm flex-1 overflow-hidden rounded-[2rem] bg-white shadow-2xl ring-1 ring-white/25" data-id="element-96" data-mp="13lf73oldlb1c">
            <img src={COLUMVI_CHANNEL_IMAGE} alt="Roche Columvi campaign shared through WhatsApp" className="h-full w-full object-cover object-[31%_center]" data-id="element-97" data-mp="1dijzi65te2ql" />
          </div>
        </motion.article>

        <motion.section initial={{
        opacity: 0,
        x: 24
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="flex min-h-0 flex-col lg:col-span-7" aria-labelledby="channel-heading" data-id="element-98" data-mp="1n7jwxl4o4q2o">
          <div className="flex items-end justify-between gap-4" data-id="element-99" data-mp="6uwjcl6ym28f">
            <div data-id="element-100" data-mp="1uqpqzh4q5np">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--brand)]" data-id="element-101" data-mp="1va4667kmbwz">
                The same Snak
              </p>
              <h3 id="channel-heading" className="mt-1 text-2xl font-extrabold text-[var(--fg)]" data-id="element-102" data-mp="3bo039ux0369">
                Ready for every relevant touchpoint
              </h3>
            </div>
            <span className="hidden rounded-full bg-[var(--brand)]/10 px-4 py-2 text-xs font-bold text-[var(--brand)] sm:inline-flex" data-id="element-103" data-mp="dberh2o6i224">
              One build · many entries
            </span>
          </div>

          <div className="mt-5 grid min-h-0 flex-1 grid-cols-2 gap-3 md:grid-cols-4" data-id="element-104" data-mp="3gsdg7q2keemx">
            {CHANNELS.map((channel, index) => {
            const Icon = channel.icon;
            return <motion.article key={channel.label} initial={{
              opacity: 0,
              y: 16
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.42 + index * 0.06,
              duration: 0.4
            }} className="group flex min-h-0 flex-col justify-between rounded-2xl border border-black/5 bg-white p-4 shadow-sm transition-transform hover:-translate-y-1" data-id="element-105" data-mp="3qnoygid6m3zf" data-mpi={index}>
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[var(--brand)]/10 text-[var(--brand)] transition-colors group-hover:bg-[var(--brand)] group-hover:text-white" data-id="element-106" data-mp="1g2z6hke2l39s">
                    <Icon className="h-5 w-5" aria-hidden="true" data-id="element-107" data-mp="164s2e24vqfp7" />
                  </span>
                  <div className="mt-4" data-id="element-108" data-mp="115f9p0rbueep">
                    <h4 className="text-base font-extrabold text-[var(--fg)]" data-id="element-109" data-mp="1168hm520v9yf">{channel.label}</h4>
                    <p className="mt-1 text-[10px] leading-snug text-[var(--muted)]" data-id="element-110" data-mp="wdx582q3hvoz">{channel.detail}</p>
                  </div>
                </motion.article>;
          })}
          </div>

          <div className="mt-4 flex items-center gap-3 rounded-2xl border border-[var(--brand)]/15 bg-[var(--brand)]/5 px-5 py-3" data-id="element-111" data-mp="nfbomd46oqqu">
            <Share2Icon className="h-5 w-5 flex-shrink-0 text-[var(--brand)]" aria-hidden="true" data-id="element-112" data-mp="ih1xqephscb1" />
            <p className="text-sm font-semibold text-[var(--fg)]" data-id="element-113" data-mp="dhxjh9npnodu">
              Consistent experience, consistent measurement—regardless of where the journey begins.
            </p>
          </div>
        </motion.section>
      </div>
    </InteriorSlide>;
}